import { useCallback, useEffect, useState } from "react";
import BinScanner from "@/components/feature/BinScanner";
import OpsHeader from "@/components/feature/OpsHeader";
import ProductScanner from "@/components/feature/ProductScanner";
import { useAuth } from "@/hooks/useAuth";
import { useOpsWarehouse } from "@/hooks/useOpsWarehouse";
import {
  errorText,
  opsApi,
  qtyText,
  type BinContent,
  type OpsWarehouse,
  type ScannedBin,
} from "@/lib/warehouseOps";

/**
 * Luân chuyển hàng theo vị trí: quét ô nguồn, chọn đúng lô đang cầm, quét ô đích.
 *
 * Đích mặc định là ô khác trong cùng kho; chọn kho khác thì thành điều chuyển
 * kho. Cả hai đều KHÔNG sinh bút toán chi phí — hàng vẫn nằm trong công ty —
 * nên máy cầm tay làm thẳng, không cần lập phiếu chờ duyệt.
 *
 * Chọn lô là chọn đúng dòng tồn của ô nguồn, không phải chọn mặt hàng rồi để hệ
 * thống tự tìm lô: người cầm máy đang cầm đúng thùng hàng đó trên tay.
 *
 * Chọn xong vẫn phải quét mã hàng để đối chiếu. Một ô chứa được nhiều mặt hàng,
 * và dòng bấm nhầm thì hàng đi sang ô khác mà sổ sách vẫn phẳng lì.
 */
export default function OpsRelocatePage() {
  const warehouse = useOpsWarehouse();
  const { getToken } = useAuth();
  const token = getToken() ?? undefined;

  const [source, setSource] = useState<ScannedBin | null>(null);
  const [moving, setMoving] = useState<BinContent | null>(null);
  const [qty, setQty] = useState("");
  const [destWarehouseId, setDestWarehouseId] = useState("");
  const [warehouses, setWarehouses] = useState<OpsWarehouse[]>([]);
  const [destBin, setDestBin] = useState<ScannedBin | null>(null);
  /** Đã quét đúng mã hàng của dòng đang chuyển chưa. */
  const [productConfirmed, setProductConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (warehouse) setDestWarehouseId((cur) => cur || warehouse.id);
  }, [warehouse]);

  // Danh sách kho chỉ cần khi người dùng muốn chuyển sang kho khác.
  useEffect(() => {
    if (!moving || warehouses.length > 0) return;

    void opsApi.warehouses(token)
      .then((res) => setWarehouses(res.data ?? []))
      .catch(() => setWarehouses([]));
  }, [moving, warehouses.length, token]);

  /** Đọc lại ô nguồn sau khi chuyển, để số tồn trên màn khớp với thực tế. */
  const refreshSource = useCallback(async () => {
    if (!warehouse || !source) return;

    try {
      const res = await opsApi.scanBin(warehouse.id, source.code, token);
      setSource(res.data);
    } catch {
      // Đọc lại hỏng thì quay về bước quét, hơn là hiện số tồn đã cũ.
      setSource(null);
    }
  }, [warehouse, source, token]);

  const startMove = (c: BinContent) => {
    setMoving(c);
    setQty(String(c.qty));
    setDestBin(null);
    setProductConfirmed(false);
    setError("");
    setFlash("");
    setDestWarehouseId(warehouse?.id ?? "");
  };

  const submit = async () => {
    if (!warehouse || !moving || !destBin || !productConfirmed) return;

    const amount = Number(qty.replace(",", "."));

    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Số lượng chuyển phải lớn hơn 0.");
      return;
    }

    if (amount > moving.qty) {
      setError(`Ô nguồn chỉ còn ${qtyText(moving.qty)} ${moving.unit}.`);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const res = await opsApi.relocate(
        {
          warehouseId: warehouse.id,
          destWarehouseId: destWarehouseId !== warehouse.id ? destWarehouseId : undefined,
          moves: [{ binContentId: moving.id, destBinId: destBin.id, qty: amount }],
        },
        token,
      );

      setFlash(res.message || `Đã chuyển ${moving.productCode} sang ô ${destBin.code}.`);
      setMoving(null);
      setDestBin(null);
      setProductConfirmed(false);
      await refreshSource();
    } catch (e) {
      const detail = (e as { errors?: string[] })?.errors?.[0];
      setError(detail || errorText(e, "Không chuyển được. Thử lại."));
    } finally {
      setSaving(false);
    }
  };

  if (!warehouse) return null;

  const sameWarehouse = destWarehouseId === warehouse.id;
  const destName = warehouses.find((w) => w.id === destWarehouseId)?.name ?? warehouse.name;

  // ─── Bước 1: quét ô nguồn ─────────────────────────────────────────────────
  if (!source) {
    return (
      <div className="min-h-screen bg-slate-50">
        <OpsHeader title="Luân chuyển nội bộ" subtitle="Bước 1 / 3" warehouseName={warehouse.name} />
        <div className="p-4 space-y-3">
          <BinScanner
            warehouseId={warehouse.id}
            token={token}
            label="Quét mã ô đang chứa hàng"
            hint="Quét xong máy sẽ hiện những gì đang nằm trong ô. Tem mờ thì gõ vài ký tự và chọn ô trong danh sách."
            searchable
            onScanned={(bin) => { setSource(bin); setError(""); }}
          />
          {flash && (
            <p className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">{flash}</p>
          )}
        </div>
      </div>
    );
  }

  // ─── Bước 3: chọn số lượng, kho đích và quét ô đích ───────────────────────
  if (moving) {
    return (
      <div className="min-h-screen bg-slate-50 pb-6">
        <OpsHeader
          title="Chuyển sang ô khác"
          subtitle="Bước 3 / 3"
          warehouseName={warehouse.name}
          onBack={() => { setMoving(null); setDestBin(null); setProductConfirmed(false); setError(""); }}
        />

        <div className="p-4 space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <p className="font-semibold text-slate-900">{moving.productName || moving.productCode}</p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">
              {moving.productCode}
              {moving.lotNumber ? ` · Lô ${moving.lotNumber}` : ""}
              {moving.expiryDate ? ` · HSD ${moving.expiryDate}` : ""}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Từ ô <span className="font-mono text-slate-700">{source.code}</span> &middot; đang có{" "}
              {qtyText(moving.qty)} {moving.unit}
            </p>
          </div>

          {!productConfirmed ? (
            <ProductScanner
              token={token}
              label="Quét mã hàng trên thùng đang cầm"
              hint="Quét đúng mặt hàng của dòng vừa chọn thì mới chuyển được."
              expected={[moving.productCode]}
              expectedLabel={`Dòng vừa chọn là ${moving.productCode}.`}
              onScanned={() => { setProductConfirmed(true); setError(""); }}
            />
          ) : (
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3">
              <p className="text-xs text-emerald-700">Đã quét đúng mã hàng</p>
              <p className="font-mono text-sm text-emerald-900">{moving.productCode}</p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Số lượng chuyển ({moving.unit})
            </label>
            <input
              inputMode="decimal"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-full px-3 py-3 text-base border-2 border-slate-200 rounded-lg focus:outline-none focus:border-brand-500"
            />
            <button
              onClick={() => setQty(String(moving.qty))}
              className="mt-2 text-sm font-medium text-brand-700"
            >
              Chuyển cả {qtyText(moving.qty)} {moving.unit}
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Kho nhận</label>
            <select
              value={destWarehouseId}
              onChange={(e) => { setDestWarehouseId(e.target.value); setDestBin(null); }}
              className="w-full px-3 py-3 text-base border-2 border-slate-200 rounded-lg bg-white"
            >
              <option value={warehouse.id}>{warehouse.name} (kho đang đứng)</option>
              {warehouses
                .filter((w) => w.id !== warehouse.id)
                .map((w) => (
                  <option key={w.id} value={w.id}>{w.fullName || w.name}</option>
                ))}
            </select>
            {!sameWarehouse && (
              <p className="mt-2 text-xs text-amber-700">
                Khác kho: ghi thành điều chuyển kho, không phải dời vị trí.
              </p>
            )}
          </div>

          {destBin ? (
            <div className="bg-white rounded-xl border-2 border-brand-400 p-3">
              <p className="text-xs text-slate-500">Ô đích đã quét</p>
              <p className="font-mono text-sm text-slate-900 break-all">{destBin.code}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {destName}
                {destBin.zoneName ? ` · Khu ${destBin.zoneName}` : ""}
              </p>
              <button onClick={() => setDestBin(null)} className="mt-2 text-sm font-medium text-brand-700">
                Quét ô đích khác
              </button>
            </div>
          ) : (
            <BinScanner
              warehouseId={destWarehouseId}
              token={token}
              label="Quét mã ô đích"
              hint={sameWarehouse ? "Phải là ô khác với ô nguồn." : `Ô thuộc kho ${destName}.`}
              onScanned={(bin) => {
                if (sameWarehouse && bin.id === source.id) {
                  setError("Ô đích trùng ô nguồn.");
                  return;
                }
                setError("");
                setDestBin(bin);
              }}
            />
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <button
            onClick={() => void submit()}
            disabled={!destBin || !productConfirmed || saving}
            className={`w-full py-3.5 rounded-xl font-semibold text-base text-white ${
              !destBin || !productConfirmed || saving ? "bg-slate-300" : "bg-brand-600"
            }`}
          >
            {saving
              ? "Đang chuyển..."
              : !productConfirmed
                ? "Quét mã hàng trước"
                : !destBin
                  ? "Quét ô đích trước"
                  : "Xác nhận chuyển"}
          </button>
        </div>
      </div>
    );
  }

  // ─── Bước 2: chọn hàng trong ô nguồn ──────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 pb-6">
      <OpsHeader
        title={`Ô ${source.code}`}
        subtitle="Bước 2 / 3 · chọn hàng cần chuyển"
        warehouseName={warehouse.name}
        onBack={() => { setSource(null); setFlash(""); setError(""); }}
      />

      <div className="p-4 space-y-3">
        {flash && (
          <p className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">{flash}</p>
        )}
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            {source.zoneName ? `Khu ${source.zoneName} · ` : ""}
            {source.contents.length} dòng tồn
          </p>
          <button onClick={() => setSource(null)} className="text-sm font-medium text-brand-700">
            Quét ô khác
          </button>
        </div>

        {source.contents.length === 0 ? (
          <p className="text-center text-slate-400 py-12">Ô này đang trống.</p>
        ) : (
          source.contents.map((c) => (
            <button
              key={c.id}
              onClick={() => startMove(c)}
              className="w-full text-left bg-white rounded-xl border border-slate-200 p-3 active:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-slate-900 text-sm truncate">
                  {c.productName || c.productCode}
                </p>
                <span className="shrink-0 text-xs text-slate-600">
                  {qtyText(c.qty)} {c.unit}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-mono mt-0.5">
                {c.productCode}
                {c.lotNumber ? ` · Lô ${c.lotNumber}` : ""}
                {c.expiryDate ? ` · HSD ${c.expiryDate}` : ""}
              </p>
              {c.reservedQty > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">
                  Đang giữ cho phiếu khác: {qtyText(c.reservedQty)} {c.unit}
                </p>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
